// Pure form-state model + serialisers for the contract Rate Card editor — the
// dependency-light leaf behind RateCard.tsx. Kept free of React and the
// api-client so the row-array state, validation, and the full-replace
// serialisation are unit-testable in isolation and so extracting it couples
// no vi.mock() factory to this file.
//
// The editor edits money as text (so a half-typed "12." is a valid interim
// state) and models a "variable price" as a blank unit-price field rather
// than a separate toggle — a blank unitPrice serialises to `null`, the
// backend's signal that the price is entered fresh on each invoice. Rows
// carry a required id (financeSetupPutSchema is a full-replace PUT body, so
// the client owns the complete rateCard array incl. ids): existing ids
// round-trip on edit and a fresh, unique id is minted for every new row.

import { parseMoney } from "./poFormState";
import type { RateItem } from "../../../types/finance";

export { parseMoney } from "./poFormState";

// A stored rate item survives a full-replace PUT only if it keeps its id,
// unlike the client-only React keys used elsewhere this id is the wire id.
// New rows mint a globally-unique id so two rows added in separate edit
// sessions can never collide. The app only runs in a secure context
// (https/localhost), where crypto.randomUUID is always available.
function newRateItemId(): string {
  return crypto.randomUUID();
}

// Client-only React list-identity key — never sent to the backend. Distinct
// from `id` so a fresh row can carry a stable `key` before it has been saved.
let keyCounter = 0;
function nextKey(): string {
  keyCounter += 1;
  return `rateitem-${keyCounter}`;
}

export interface RateItemDraft {
  /** Client-only React key; not part of the wire payload. */
  key: string;
  /** Wire id — preserved on edit, minted fresh for a new row. */
  id: string;
  item: string;
  description: string;
  unit: string;
  /** Money as text; a blank value means the price is variable (null on the wire). */
  unitPrice: string;
  gstApplies: boolean;
}

export interface RateItemFieldErrors {
  /** Per-row messages, keyed by the draft's client key. */
  [key: string]: string;
}

export function makeEmptyRateItem(): RateItemDraft {
  return {
    key: nextKey(),
    id: newRateItemId(),
    item: "",
    description: "",
    unit: "",
    unitPrice: "",
    gstApplies: true,
  };
}

export function stateFromRateCard(items: RateItem[]): RateItemDraft[] {
  return items.map((row) => ({
    key: nextKey(),
    id: row.id,
    item: row.item,
    description: row.description ?? "",
    unit: row.unit,
    unitPrice: row.unitPrice === null ? "" : String(row.unitPrice),
    gstApplies: row.gstApplies,
  }));
}

export function addRateItem(drafts: RateItemDraft[]): RateItemDraft[] {
  return [...drafts, makeEmptyRateItem()];
}

export function removeRateItem(drafts: RateItemDraft[], index: number): RateItemDraft[] {
  return drafts.filter((_, i) => i !== index);
}

export function patchRateItem(
  drafts: RateItemDraft[],
  index: number,
  patch: Partial<RateItemDraft>,
): RateItemDraft[] {
  return drafts.map((row, i) => (i === index ? { ...row, ...patch } : row));
}

export function validateRateCard(drafts: RateItemDraft[]): RateItemFieldErrors {
  const errors: RateItemFieldErrors = {};
  for (const row of drafts) {
    if (!row.item.trim()) {
      errors[row.key] = "Item is required";
    } else if (!row.unit.trim()) {
      errors[row.key] = "Unit is required";
    }
  }
  return errors;
}

export function hasRateCardErrors(errors: RateItemFieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

export function serialiseRateCard(drafts: RateItemDraft[]): RateItem[] {
  return drafts.map((row) => {
    const out: RateItem = {
      id: row.id,
      item: row.item.trim(),
      unit: row.unit.trim(),
      unitPrice: parseMoney(row.unitPrice),
      gstApplies: row.gstApplies,
    };
    if (row.description.trim()) out.description = row.description.trim();
    return out;
  });
}
